import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'
import ReactDOM from 'react-dom'
import ReactMarkdown from 'react-markdown'

import { Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { ImageLoader } from 'browser/components/atomic-elements/atoms/image-loader/image-loader'

import {
  FileViewerProps,
  THUMBNAIL_SIZE_PRESETS,
  ThumbnailSizePreset,
} from 'shared-libs/components/ui2/fields/field-ui/ui2-file-viewer/ui2-file-viewer-props.ts'

import './_file-viewer.scss'

const DEFAULT_PLACEHOLDER = 'No image available.'

export interface IUI2FileViewerProps extends FileViewerProps, Omit<IBaseProps, 'children'> {}

interface IUI2FileViewerState {
  carouselIndex: number
  lightboxIndex: number | null
}

export class UI2FileViewer extends React.PureComponent<IUI2FileViewerProps, IUI2FileViewerState> {
  public static defaultProps: Partial<IUI2FileViewerProps> = {
    thumbnailSize: 'md',
    thumbnailFit: 'contain',
    showFileName: false,
    showDownloadButton: false,
    showPrintButton: false,
  }

  public state: IUI2FileViewerState = { carouselIndex: 0, lightboxIndex: null }

  public componentDidUpdate(prevProps: IUI2FileViewerProps) {
    if (prevProps.value !== this.props.value || prevProps.uri !== this.props.uri) {
      this.setState({ carouselIndex: 0 })
    }
  }

  public render() {
    const { className, style, isDisabled } = this.props
    const images = this.getImages()
    return (
      <>
        <div
          className={classNames('ui2-fileViewer', { 'ui2-fileViewer--disabled': isDisabled }, className)}
          style={style}
        >
          {_.isEmpty(images) ? this.renderEmptyState() : this.renderViewer(images)}
        </div>
        {this.renderLightbox()}
      </>
    )
  }

  private renderEmptyState(): React.ReactNode {
    const { placeholder } = this.props
    const message = placeholder || DEFAULT_PLACEHOLDER
    const height = this.resolveThumbnailHeight()
    return (
      <div className='ui2-fileViewer-emptyState' style={{ minHeight: height }}>
        <ReactMarkdown>{message}</ReactMarkdown>
      </div>
    )
  }

  private renderViewer(images: any[]): React.ReactNode {
    const { showFileName, thumbnailFit } = this.props
    const total = images.length
    const carouselIndex = Math.min(this.state.carouselIndex, total - 1)
    const current = images[carouselIndex]
    const height = this.resolveThumbnailHeight()
    const fitClass = `ui2-fileViewer-thumbnailWrap--${thumbnailFit}`
    return (
      <div className={classNames('ui2-fileViewer-thumbnailWrap', fitClass)} style={{ height }}>
        <ImageLoader
          src={current.uri}
          className='ui2-fileViewer-image'
          onClick={() => this.handleOpenLightbox(carouselIndex)}
        />
        {total > 1 && (
          <>
            {carouselIndex > 0 && (
              <button
                className='ui2-fileViewer-nav ui2-fileViewer-nav--left'
                type='button'
                onClick={this.handlePrev}
                aria-label='Previous file'
              >
                <Icon icon={IconNames.CHEVRON_LEFT} iconSize={20} />
              </button>
            )}
            {carouselIndex < total - 1 && (
              <button
                className='ui2-fileViewer-nav ui2-fileViewer-nav--right'
                type='button'
                onClick={this.handleNext}
                aria-label='Next file'
              >
                <Icon icon={IconNames.CHEVRON_RIGHT} iconSize={20} />
              </button>
            )}
            <div className='ui2-fileViewer-indicator'>
              {carouselIndex + 1} of {total}
            </div>
          </>
        )}
        {showFileName && current.name && (
          <div className='ui2-fileViewer-fileNameBadge' title={current.name}>
            {current.name}
          </div>
        )}
        {this.renderActionBar(current, 'overlay')}
      </div>
    )
  }

  // Download/print/zoom buttons. `overlay` variant floats on the inline thumbnail;
  // `lightbox` variant sits in the lightbox toolbar (larger icons, inverted colors).
  private renderActionBar(current: any, variant: 'overlay' | 'lightbox'): React.ReactNode {
    const { showDownloadButton, showPrintButton, onRemoveCurrent, isDisabled } = this.props
    // Download / print / remove / zoom are all interactions; a disabled
    // field offers none of them.
    if (isDisabled) return null
    const includeZoom = variant === 'overlay'
    if (!includeZoom && !showDownloadButton && !showPrintButton && !onRemoveCurrent) return null
    const iconSize = variant === 'overlay' ? 16 : 18
    const barClass = classNames('ui2-fileViewer-actionBar', `ui2-fileViewer-actionBar--${variant}`)
    return (
      <div className={barClass}>
        {showDownloadButton && (
          <button
            className='ui2-fileViewer-actionBarButton'
            type='button'
            onClick={(e) => { e.stopPropagation(); this.handleDownload(current) }}
            aria-label='Download'
            title='Download'
          >
            <Icon icon={IconNames.DOWNLOAD} iconSize={iconSize} />
          </button>
        )}
        {showPrintButton && (
          <button
            className='ui2-fileViewer-actionBarButton'
            type='button'
            onClick={(e) => { e.stopPropagation(); this.handlePrint(current) }}
            aria-label='Print'
            title='Print'
          >
            <Icon icon={IconNames.PRINT} iconSize={iconSize} />
          </button>
        )}
        {includeZoom && (
          <button
            className='ui2-fileViewer-actionBarButton'
            type='button'
            onClick={(e) => { e.stopPropagation(); this.handleOpenLightbox(this.state.carouselIndex) }}
            aria-label='Open lightbox'
            title='Open lightbox'
          >
            <Icon icon={IconNames.ZOOM_IN} iconSize={iconSize} />
          </button>
        )}
        {onRemoveCurrent && (
          <button
            className='ui2-fileViewer-actionBarButton ui2-fileViewer-actionBarButton--danger'
            type='button'
            onClick={(e) => { e.stopPropagation(); onRemoveCurrent(this.state.carouselIndex) }}
            aria-label='Remove file'
            title='Remove file'
          >
            <Icon icon={IconNames.TRASH} iconSize={iconSize} />
          </button>
        )}
      </div>
    )
  }

  private renderLightbox(): React.ReactNode {
    const { lightboxIndex } = this.state
    if (lightboxIndex === null || typeof document === 'undefined') return null
    const images = this.getImages()
    if (_.isEmpty(images)) return null
    const total = images.length
    const index = Math.min(lightboxIndex, total - 1)
    const current = images[index]
    return ReactDOM.createPortal(
      <UI2FileViewerLightbox
        images={images}
        index={index}
        current={current}
        total={total}
        onPrev={this.handleLightboxPrev}
        onNext={this.handleLightboxNext}
        onClose={this.handleCloseLightbox}
        actions={this.renderActionBar(current, 'lightbox')}
      />,
      document.body,
    )
  }

  private handlePrev = () => {
    this.setState(({ carouselIndex }) => ({ carouselIndex: Math.max(0, carouselIndex - 1) }))
  }

  private handleNext = () => {
    const total = this.getImages().length
    this.setState(({ carouselIndex }) => ({ carouselIndex: Math.min(total - 1, carouselIndex + 1) }))
  }

  // Single gate for every path into the lightbox — the thumbnail, the zoom
  // button, and anything added later. A disabled field that still opens a
  // full-screen viewer isn't disabled, it only looks it.
  private handleOpenLightbox = (index: number) => {
    if (this.props.isDisabled) return
    this.setState({ lightboxIndex: index })
  }

  private handleCloseLightbox = () => {
    this.setState({ lightboxIndex: null })
  }

  private handleLightboxPrev = () => {
    this.setState(({ lightboxIndex }) => ({
      lightboxIndex: lightboxIndex === null ? null : Math.max(0, lightboxIndex - 1),
    }))
  }

  private handleLightboxNext = () => {
    const total = this.getImages().length
    this.setState(({ lightboxIndex }) => ({
      lightboxIndex: lightboxIndex === null ? null : Math.min(total - 1, lightboxIndex + 1),
    }))
  }

  // Download via fetch → blob → object URL so the `download` attribute is honored
  // even for cross-origin images when CORS allows. Falls back to navigation.
  private handleDownload = async (current: any) => {
    if (!current?.uri) return
    const fileName = current.name || extractFileName(current.uri) || 'download'
    try {
      const res = await fetch(current.uri, { mode: 'cors' })
      if (!res.ok) throw new Error('fetch failed')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch {
      window.open(current.uri, '_blank', 'noopener,noreferrer')
    }
  }

  // Print via hidden iframe. When `printOptions.printablePath` is set and the
  // entity has an aggregate PDF at that path, prints the PDF (parity with NM
  // PrintUtils); otherwise prints the visible image.
  private handlePrint = (current: any) => {
    const url = this.resolvePrintUrl(current)
    if (!url) return
    const iframe = document.createElement('iframe')
    iframe.setAttribute('aria-hidden', 'true')
    Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0', visibility: 'hidden' })
    const triggerPrint = () => {
      try {
        iframe.contentWindow?.focus()
        iframe.contentWindow?.print()
      } finally {
        setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe) }, 1000)
      }
    }
    if (this.isPdfUrl(url)) {
      iframe.src = url
      iframe.addEventListener('load', triggerPrint)
      document.body.appendChild(iframe)
      return
    }
    document.body.appendChild(iframe)
    const doc = iframe.contentDocument
    if (!doc) { document.body.removeChild(iframe); return }
    doc.open()
    doc.write(`<!doctype html><html><head><title>Print</title>
      <style>@page{margin:12mm}html,body{margin:0;padding:0}img{display:block;max-width:100%;height:auto}</style>
      </head><body><img src="${url}" /></body></html>`)
    doc.close()
    const img = doc.querySelector('img')
    if (img && !img.complete) {
      img.addEventListener('load', triggerPrint)
      img.addEventListener('error', triggerPrint)
    } else {
      triggerPrint()
    }
  }

  private resolvePrintUrl(current: any): string | undefined {
    const { entity, printOptions } = this.props
    if (entity && printOptions?.printablePath) {
      const aggregateUri = _.get(entity, [...printOptions.printablePath.split('.'), 'aggregate', 'uri'])
      if (aggregateUri) return aggregateUri
    }
    return current?.uri
  }

  private isPdfUrl(url: string): boolean {
    try {
      return new URL(url).pathname.toLowerCase().endsWith('.pdf')
    } catch {
      return url.toLowerCase().includes('.pdf')
    }
  }

  private getImages(): any[] {
    const { uri, value } = this.props
    if (_.isString(uri) && !_.isEmpty(uri)) return [{ uri, type: 'image' }]
    if (_.isArray(uri)) {
      return uri
        .filter((entry) => _.isString(entry) && !_.isEmpty(entry))
        .map((u: string) => ({ uri: u, type: 'image' }))
    }
    if (_.isString(value) && !_.isEmpty(value)) return [{ uri: value, type: 'image' }]
    if (_.isArray(value)) {
      return value
        .map((entry: any) => {
          if (_.isString(entry) && !_.isEmpty(entry)) return { uri: entry, type: 'image' }
          if (entry && _.isString(entry.uri) && !_.isEmpty(entry.uri)) {
            return { ...entry, type: entry.type || 'image' }
          }
          return null
        })
        .filter(Boolean) as any[]
    }
    const images: any[] = []
    const files = _.get(value, 'files', [])
    _.forEach(files, (file: any) => {
      if (file.type === 'image') images.push(file)
      else if (file.type === 'pdf') Array.prototype.push.apply(images, file.preview || [])
    })
    return images
  }

  private resolveThumbnailHeight(): number {
    const { thumbnailSize } = this.props
    if (typeof thumbnailSize === 'number') return thumbnailSize
    return THUMBNAIL_SIZE_PRESETS[thumbnailSize as ThumbnailSizePreset] ?? THUMBNAIL_SIZE_PRESETS.md
  }
}

function extractFileName(uri: string): string | undefined {
  try {
    const pathname = new URL(uri, window.location.href).pathname
    const last = pathname.split('/').filter(Boolean).pop()
    return last ? decodeURIComponent(last) : undefined
  } catch {
    return undefined
  }
}

// ─── Lightbox ────────────────────────────────────────────────────────────────
// Exported so siblings (e.g. UI2FileUploadField chip list) can reuse the same
// portal-rendered lightbox UI when their own affordances are tapped.

interface IUI2FileViewerLightboxProps {
  images: any[]
  index: number
  current: any
  total: number
  onPrev: () => void
  onNext: () => void
  onClose: () => void
  actions?: React.ReactNode
}

interface IUI2FileViewerLightboxState {
  isZoomed: boolean
}

export class UI2FileViewerLightbox extends React.PureComponent<IUI2FileViewerLightboxProps, IUI2FileViewerLightboxState> {
  public state: IUI2FileViewerLightboxState = { isZoomed: false }

  public componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown)
    document.body.style.overflow = 'hidden'
  }

  public componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown)
    document.body.style.overflow = ''
  }

  public componentDidUpdate(prevProps: IUI2FileViewerLightboxProps) {
    if (prevProps.current?.uri !== this.props.current?.uri) {
      this.setState({ isZoomed: false })
    }
  }

  public render() {
    const { current, total, index } = this.props
    const { isZoomed } = this.state
    return (
      <div className='ui2-fileViewer-lightbox' role='dialog' aria-modal='true'>
        <div className='ui2-fileViewer-lightbox-backdrop' onClick={this.props.onClose} />
        <div className='ui2-fileViewer-lightbox-content'>
          <img
            className={classNames('ui2-fileViewer-lightbox-image', {
              'ui2-fileViewer-lightbox-image--zoomed': isZoomed,
            })}
            src={current?.uri}
            alt={current?.name || ''}
            onClick={this.toggleZoom}
          />
        </div>
        <div className='ui2-fileViewer-lightbox-toolbar'>
          {this.props.actions}
          <button
            className='ui2-fileViewer-lightbox-close'
            type='button'
            onClick={this.props.onClose}
            aria-label='Close'
          >
            <Icon icon={IconNames.CROSS} iconSize={20} />
          </button>
        </div>
        {total > 1 && (
          <>
            {index > 0 && (
              <button
                className='ui2-fileViewer-lightbox-nav ui2-fileViewer-lightbox-nav--left'
                type='button'
                onClick={this.props.onPrev}
                aria-label='Previous'
              >
                <Icon icon={IconNames.CHEVRON_LEFT} iconSize={28} />
              </button>
            )}
            {index < total - 1 && (
              <button
                className='ui2-fileViewer-lightbox-nav ui2-fileViewer-lightbox-nav--right'
                type='button'
                onClick={this.props.onNext}
                aria-label='Next'
              >
                <Icon icon={IconNames.CHEVRON_RIGHT} iconSize={28} />
              </button>
            )}
            <div className='ui2-fileViewer-lightbox-indicator'>
              {index + 1} of {total}
            </div>
          </>
        )}
      </div>
    )
  }

  private toggleZoom = () => {
    this.setState((prev) => ({ isZoomed: !prev.isZoomed }))
  }

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); this.props.onClose() }
    else if (e.key === 'ArrowLeft') this.props.onPrev()
    else if (e.key === 'ArrowRight') this.props.onNext()
  }
}
